import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

// PKCE Helper Functions
const base64URLEncode = (buffer: Uint8Array): string => {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(hash));
};

export const QRLogin = () => {
  const sessionId = window.location.pathname.split('/qr-login/')[1];
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initPKCEFlow = async () => {
      console.log('[QRLogin] Current pathname:', window.location.pathname);
      console.log('[QRLogin] Extracted sessionId:', sessionId);
      
      if (!sessionId || sessionId.includes('/')) {
        console.error('[QRLogin] Invalid or missing session ID in URL');
        setError('Invalid session ID');
        return;
      }

      console.log('[QRLogin] Starting PKCE flow...');

      // Generate PKCE parameters
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      console.log('[QRLogin] Code verifier generated:', codeVerifier.substring(0, 10) + '...');
      console.log('[QRLogin] Code challenge generated:', codeChallenge.substring(0, 10) + '...');

      // Store verifier for later use in callback
      localStorage.setItem(`pkce_verifier_${sessionId}`, codeVerifier);
      console.log('[QRLogin] Stored verifier in localStorage');

      // Build OAuth URL with PKCE
      const clientId = '5eaa225e-61a5-407a-93b0-20d0eb819191';
      const redirectUri = encodeURIComponent('https://email2contact.com/auth-callback-microsoft');
      const scopes = encodeURIComponent('openid profile User.Read Contacts.ReadWrite MailboxSettings.ReadWrite');

      const msAuthUrl = 
        `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        `client_id=${clientId}` +
        `&response_type=code` +
        `&redirect_uri=${redirectUri}` +
        `&scope=${scopes}` +
        `&state=${sessionId}` +
        `&response_mode=query` +
        `&code_challenge=${codeChallenge}` +
        `&code_challenge_method=S256`;

      console.log('[QRLogin] Redirecting to Microsoft OAuth with PKCE...');

      // Redirect to Microsoft login
      window.location.href = msAuthUrl;
    };

    initPKCEFlow().catch(err => {
      console.error('[QRLogin] PKCE flow error:', err);
      setError('Failed to initialize authentication');
    });
  }, [sessionId]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background">
        <p className="text-lg text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background">
      <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
      <p className="text-lg text-muted-foreground">Redirecting to Microsoft login...</p>
    </div>
  );
};
